import { add, freeze, hash, integer } from '../kernel/primitives.ts';
import { findPath, hasLineOfSight, isWalkable, metricDistance, validateNavigationMap, type NavigationMap, type NavPosition, type PathStep } from '../navigation/index.ts';
import type { AcceptedPcIntent } from '../pc-actions/index.ts';
import type { TacticalEvent } from '@dissidium/contracts';
import { createTactics, stepTactics, type CombatSetup, type TacticalState } from './tactics.ts';
export { ANSWERS, answerStart, availableCombatOptions, committedForce, windupAnswer, type WindupAdvice, type CombatSetup, type TacticalState } from './tactics.ts';

export type CombatAction =
  | Readonly<{ type: 'move'; destination: NavPosition }>
  | Readonly<{ type: 'attack'; targetId: string }>;
export type ActiveCombatAction =
  | { type: 'move'; destination: NavPosition; route: PathStep[]; remainingTicks: number }
  | { type: 'attack'; targetId: string; remainingTicks: number };
export type CombatActor = {
  id: string;
  role: 'pc' | 'companion' | 'enemy';
  position: NavPosition;
  hp: number;
  maxHp: number;
  movePerTick: number;
  attackDamage: number;
  attackRange: number;
  attackWindupTicks: number;
  status: 'alive' | 'dead';
  activeAction: ActiveCombatAction | null;
};
export type CombatEvent =
  | TacticalEvent
  | { type: 'moved'; tick: number; actorId: string; position: NavPosition }
  | { type: 'hit'; tick: number; actorId: string; targetId: string; damage: number }
  | { type: 'interrupted'; tick: number; actorId: string; reason: 'damage' | 'target-unavailable' | 'out-of-range' | 'no-line-of-sight' }
  | { type: 'death'; tick: number; actorId: string };
export type CombatState = {
  tick: number;
  paused: boolean;
  pcId: string;
  actors: Record<string, CombatActor>;
  pcQueue: CombatAction[];
  lastPcSequence: number;
  acceptedPcIntents: AcceptedPcIntent[];
  events: CombatEvent[];
  tactics?: TacticalState | undefined;
};
export type CombatActorInput = Omit<CombatActor, 'status' | 'activeAction'>;
export type AutonomousDecision = (state: Readonly<CombatState>, actorId: string) => CombatAction | null;

const ownKeys = (value: object): string[] => Object.keys(value).sort();
const compareText = (a: string, b: string): number => a < b ? -1 : a > b ? 1 : 0;
const exactKeys = (value: object, keys: readonly string[]): boolean =>
  ownKeys(value).join(',') === [...keys].sort().join(',');
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;

function assertPosition(value: unknown): asserts value is NavPosition {
  if (!isRecord(value) || !exactKeys(value, ['x', 'y', 'layer']) ||
      !Number.isSafeInteger(value.x) || !Number.isSafeInteger(value.y) ||
      !Number.isSafeInteger(value.layer) || Number(value.layer) < 0) throw new Error('Combat action has an invalid position');
}

export function assertCombatAction(value: unknown): asserts value is CombatAction {
  if (!isRecord(value) || typeof value.type !== 'string') throw new Error('Invalid combat action');
  if (value.type === 'move') {
    if (!exactKeys(value, ['type', 'destination'])) throw new Error('Move actions contain only a destination');
    assertPosition(value.destination);
    return;
  }
  if (value.type === 'attack') {
    if (!exactKeys(value, ['type', 'targetId']) || typeof value.targetId !== 'string' || !value.targetId) throw new Error('Attack actions need one target');
    return;
  }
  throw new Error('Unknown combat action');
}

function positive(value: number, label: string): number {
  if (integer(value) < 1) throw new Error(`${label} must be a positive integer`);
  return value;
}

export function createCombat(map: NavigationMap, inputs: readonly CombatActorInput[], pcId: string, setup?: CombatSetup): CombatState {
  validateNavigationMap(map);
  const actors: Record<string, CombatActor> = {};
  for (const input of [...inputs].sort((a, b) => compareText(a.id, b.id))) {
    if (!input.id || actors[input.id]) throw new Error('Combat actor IDs must be non-empty and unique');
    if (!['pc', 'companion', 'enemy'].includes(input.role)) throw new Error('Combat actors need a supported role');
    if (!isWalkable(map, input.position)) throw new Error(`Combat actor ${input.id} is outside authored walkability`);
    positive(input.maxHp, 'Maximum HP');
    if (integer(input.hp) < 0 || input.hp > input.maxHp) throw new Error('HP must be between zero and maximum HP');
    positive(input.movePerTick, 'Movement rate');
    positive(input.attackDamage, 'Attack damage');
    positive(input.attackRange, 'Attack range');
    positive(input.attackWindupTicks, 'Attack wind-up');
    actors[input.id] = { ...structuredClone(input), status: input.hp === 0 ? 'dead' : 'alive', activeAction: null };
  }
  if (actors[pcId]?.role !== 'pc' || Object.values(actors).filter(actor => actor.role === 'pc').length !== 1) throw new Error('Combat needs exactly one PC');
  const state: CombatState = { tick: 0, paused: false, pcId, actors, pcQueue: [], lastPcSequence: -1, acceptedPcIntents: [], events: [] };
  if (setup) state.tactics = createTactics(state, map, setup);
  return state;
}

export const combatHash = (state: CombatState): string => hash(state);

const ticksFor = (cost: number, speed: number): number => Math.floor((cost - 1) / speed) + 1;

function beginAction(map: NavigationMap, actor: CombatActor, action: CombatAction): void {
  assertCombatAction(action);
  if (action.type === 'move') {
    const route = findPath(map, actor.position, action.destination);
    if (route === null) throw new Error(`Actor ${actor.id} cannot reach the queued destination`);
    if (route.length) actor.activeAction = { type: 'move', destination: structuredClone(action.destination), route: structuredClone(route), remainingTicks: ticksFor(route[0]!.cost, actor.movePerTick) };
    return;
  }
  if (action.targetId === actor.id) throw new Error('An actor cannot attack itself');
  actor.activeAction = { type: 'attack', targetId: action.targetId, remainingTicks: actor.attackWindupTicks };
}

function interrupt(state: CombatState, actor: CombatActor, reason: Extract<CombatEvent, { type: 'interrupted' }>['reason']): void {
  actor.activeAction = null;
  state.events.push({ type: 'interrupted', tick: state.tick, actorId: actor.id, reason });
}

function move(state: CombatState, actor: CombatActor, action: Extract<ActiveCombatAction, { type: 'move' }>): void {
  action.remainingTicks--;
  if (action.remainingTicks) return;
  const step = action.route.shift()!;
  actor.position = step.position;
  state.events.push({ type: 'moved', tick: state.tick, actorId: actor.id, position: actor.position });
  if (!action.route.length) actor.activeAction = null;
  else action.remainingTicks = ticksFor(action.route[0]!.cost, actor.movePerTick);
}

function attack(state: CombatState, map: NavigationMap, actor: CombatActor, action: Extract<ActiveCombatAction, { type: 'attack' }>): void {
  action.remainingTicks--;
  if (action.remainingTicks) return;
  const target = state.actors[action.targetId];
  if (!target || target.status === 'dead') return interrupt(state, actor, 'target-unavailable');
  const distance = metricDistance(actor.position, target.position);
  if (distance === null || distance > actor.attackRange) return interrupt(state, actor, 'out-of-range');
  if (!hasLineOfSight(map, actor.position, target.position)) return interrupt(state, actor, 'no-line-of-sight');
  actor.activeAction = null;
  target.hp = Math.max(0, add(target.hp, -actor.attackDamage));
  state.events.push({ type: 'hit', tick: state.tick, actorId: actor.id, targetId: target.id, damage: actor.attackDamage });
  if (target.activeAction) interrupt(state, target, 'damage');
  if (target.hp === 0) {
    target.status = 'dead';
    target.activeAction = null;
    if (target.id === state.pcId) state.pcQueue = [];
    state.events.push({ type: 'death', tick: state.tick, actorId: target.id });
  }
}

export function stepCombat(input: CombatState, map: NavigationMap, decide: AutonomousDecision = () => null): CombatState {
  if (input.paused) return structuredClone(input);
  if (input.tactics) return stepTactics(input, map, decide);
  const state = structuredClone(input);
  state.tick = add(state.tick, 1);
  const ids = Object.keys(state.actors).sort();
  const decisionState = freeze(structuredClone(state));
  for (const id of ids) {
    const actor = state.actors[id]!;
    if (actor.status === 'dead' || actor.activeAction) continue;
    const action = actor.role === 'pc' ? state.pcQueue.shift() ?? null : decide(decisionState, actor.id);
    if (action) beginAction(map, actor, action);
  }
  for (const id of ids) {
    const actor = state.actors[id]!;
    if (actor.status === 'dead' || !actor.activeAction) continue;
    if (actor.activeAction.type === 'move') move(state, actor, actor.activeAction);
    else attack(state, map, actor, actor.activeAction);
  }
  return state;
}

export function advanceCombat(input: CombatState, map: NavigationMap, ticks: number, decide: AutonomousDecision = () => null): CombatState {
  if (integer(ticks) < 0) throw new Error('Tick count cannot be negative');
  let state = input;
  for (let count = 0; count < ticks; count++) state = stepCombat(state, map, decide);
  return state;
}
